import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, HelpCircle, Info } from "lucide-react";

const GameInstructionsModal = ({ isOpen, onClose, title, instructions }) => {
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            onWheel={(e) => e.stopPropagation()}
            className="absolute inset-0 bg-black/80 backdrop-blur-xl"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-2xl max-h-[85vh] flex flex-col bg-[var(--ink)] border border-[rgba(201,169,97,0.2)] rounded-[32px] md:rounded-[40px] shadow-[0_0_100px_rgba(201,169,97,0.1)] overflow-hidden"
          >
            {/* Header Decorations */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent opacity-30 z-50" />
            
            {/* Sticky Header */}
            <div className="p-8 md:p-12 pb-6 border-b border-white/5 bg-[var(--ink)]/80 backdrop-blur-md z-40">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border border-[var(--gold)]/20 flex items-center justify-center text-[var(--gold)]">
                    <Info size={20} className="md:w-6 md:h-6" />
                  </div>
                  <div>
                    <span className="font-impact text-[9px] md:text-[10px] tracking-[0.4em] text-[var(--gold)] uppercase">Laboratory Manual</span>
                    <h2 className="font-display text-3xl md:text-4xl text-white mt-1">{title}</h2>
                  </div>
                </div>
                <button 
                  onClick={onClose}
                  className="p-3 md:p-4 rounded-full border border-white/5 hover:border-[var(--gold)]/40 text-[var(--muted)] hover:text-[var(--gold)] transition-all duration-500"
                >
                  <X size={18} className="md:w-5 md:h-5" />
                </button>
              </div>
            </div>

            {/* Scrollable Center */}
            <div 
              className="flex-1 overflow-y-auto p-8 md:p-12 py-10 space-y-10 custom-scrollbar overscroll-contain"
              data-lenis-prevent
            >
              {instructions.map((item, i) => (
                <div key={i} className="flex gap-6 md:gap-8 group">
                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center font-impact text-[var(--gold)] group-hover:bg-[var(--gold)] group-hover:text-[var(--ink)] transition-all duration-500">
                    0{i + 1}
                  </div>
                  <div>
                    <h4 className="font-display text-lg md:text-xl text-white mb-2">{item.title}</h4>
                    <p className="text-[var(--cream-muted)] leading-relaxed font-light text-sm md:text-base">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Sticky Footer */}
            <div className="p-8 md:p-12 pt-6 border-t border-white/5 bg-[var(--ink)]/80 backdrop-blur-md z-40 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[var(--gold)] animate-pulse" />
                <span className="text-[10px] font-impact tracking-[0.2em] text-[var(--muted)] uppercase">System Ready</span>
              </div>
              <button 
                onClick={onClose}
                className="btn-primary w-full md:w-auto px-12 py-5"
              >
                Got it, Let's Play
              </button>
            </div>

            {/* Background Aesthetic */}
            <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-[var(--gold)] opacity-[0.03] blur-[80px] rounded-full pointer-events-none" />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default GameInstructionsModal;
